import { Box, Chip, Paper, Typography } from "@mui/material";
import React, { useCallback, useEffect, useState } from "react";
import { useDropzone } from "react-dropzone";
import AttachmentModel from "../../core/api/email/EmailAttachmentModel";
import { translations } from "../../translations/common";

interface Props {
  name: string;
  id: string;
  value: string[];
  onChange: (name: string, attachments: string[]) => void;
}

interface CustomFile extends File {
  id?: string;
  preview?: string;
}

const FileSelector: React.FC<Props> = ({ name, onChange }) => {
  const [files, setFiles] = useState<CustomFile[]>([]);

  const onDrop = useCallback(async (acceptedFiles: File[]) => {
    const newFiles = await Promise.all(
      acceptedFiles.map(async (file) => {
        const id = await AttachmentModel.storeAttachment(file);
        return Object.assign(file, {
          id,
          preview: URL.createObjectURL(file)
        });
      })
    );
    setFiles((prev) => [...prev, ...newFiles]);
  }, []);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    maxFiles: 5,
    maxSize: 5242880, // 5MB
  });

  const handleDelete = useCallback(async (fileToDelete: CustomFile) => {
    if (fileToDelete.id) {
      await AttachmentModel.deleteAttachment(fileToDelete.id);
    }
    setFiles((prev) => prev.filter(file => file !== fileToDelete));
  }, []);

  useEffect(() => {
    onChange(
      name,
      files.map((f) => f.id as string)
    );
  }, [files, name, onChange]);

  // Clean up previews on unmount
  useEffect(() => {
    return () => {
      files.forEach(file => {
        if (file.preview) {
          URL.revokeObjectURL(file.preview);
        }
      });
    };
  }, [files]);

  return (
    <Box>
      <Paper
        {...getRootProps()}
        sx={{
          p: 2,
          minHeight: 50,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          cursor: 'pointer',
          backgroundColor: isDragActive ? 'action.hover' : 'background.paper',
          border: '1px dashed',
          borderColor: isDragActive ? 'primary.main' : 'divider'
        }}
      >
        <input {...getInputProps()} />
        <Typography variant="body1" color="textSecondary">
          {translations.fileSelector.dropzoneText}
        </Typography>
      </Paper>

      {files.length > 0 && (
        <Box sx={{ mt: 2 }}>
          <Typography variant="subtitle2" gutterBottom>
            {translations.fileSelector.previewText}
          </Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
            {files.map((file) => (
              <Chip
                key={file.name}
                label={file.name}
                onDelete={() => handleDelete(file)}
                size="small"
              />
            ))}
          </Box>
        </Box>
      )}
    </Box>
  );
};

export default FileSelector;
